export const MAX_LEVEL = 60;

/**
 * XP needed to go from `level` to `level + 1`.
 * 100 at level 1, growing gently: 100 + 30·n + 4·n² where n = level − 1.
 * Returns 0 at the cap.
 */
export function xpToNextLevel(level: number): number {
  if (!Number.isInteger(level) || level < 1) {
    throw new RangeError(`level must be an integer >= 1, got ${level}`);
  }
  if (level >= MAX_LEVEL) return 0;
  const n = level - 1;
  return 100 + 30 * n + 4 * n * n;
}

/** Cumulative XP required to have reached `level` (level 1 = 0). */
export function totalXpForLevel(level: number): number {
  const target = Math.min(Math.max(1, Math.floor(level)), MAX_LEVEL);
  let sum = 0;
  for (let l = 1; l < target; l++) sum += xpToNextLevel(l);
  return sum;
}

export interface LevelInfo {
  level: number;
  /** XP earned since reaching `level`. 0 at the cap. */
  xpIntoLevel: number;
  /** XP needed to reach the next level. 0 at the cap. */
  xpForNext: number;
  /** 0..1 fill of the level bar. 1 at the cap. */
  progress: number;
  totalXp: number;
}

export function levelFromXp(xp: number): LevelInfo {
  const totalXp = Math.max(0, Math.floor(Number.isFinite(xp) ? xp : 0));
  let level = 1;
  let remaining = totalXp;
  while (level < MAX_LEVEL && remaining >= xpToNextLevel(level)) {
    remaining -= xpToNextLevel(level);
    level++;
  }
  if (level >= MAX_LEVEL) {
    return { level: MAX_LEVEL, xpIntoLevel: 0, xpForNext: 0, progress: 1, totalXp };
  }
  const xpForNext = xpToNextLevel(level);
  return { level, xpIntoLevel: remaining, xpForNext, progress: remaining / xpForNext, totalXp };
}
